import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Common from "us.common";
import "./CreditorRemitDetailsTable.scss";
import { CreditorRemitDetailsProps } from "./Interfaces";
import { RemitDetailsTable } from "./Components";
import { getRouteUrl } from "us.helper";
import { connect, ConnectedProps } from "react-redux";
import { getTotalRemitAmount } from "us.collection.economy/components/Remit/Functions";
import { RootState } from "us.helper/types";
import { LeftOutlined, RightOutlined } from "us.icons";
1;
import { Formik } from "formik";
import {
  fetchCreditorNumber,
  getCreditorCaseList,
  getCreditorRemitDetails,
} from "./Functions";

const {
  $Divider,
  $Button,
  $Select,
  $AmountLabel,
  $Skeleton,
  $AccountNoLabel,
  $Col,
  $Row,
  $Popover,
} = Common.Components;

/**
 * @description - Selected creditor remit details table
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/2858385532/Remit+Details+UI+Design+and+Implementation
 * @author Lashini Ayesha <lashinia@unicorn-solutions.com>
 * @since 14/06/2024
 */
const CreditorRemitDetails: React.FC<
  CreditorRemitDetailsProps & PropsFromRedux
> = (props) => {
  const { t } = useTranslation();
  const {
    remitSummaryByCreditor,
    selectedRemitRecord,
    remitMetaData,
    remittedDetails,
    handleNavigation,
  } = props;
  const { creditor, vatStatus, remitAccountNo, remitDetails } =
    remitSummaryByCreditor.data ?? {};
  const { journalId, accountNo, voucherNo } = remitMetaData;

  const { prevCreditorNo, nextCreditorNo } = useMemo(() => {
    const prevCreditorNo = fetchCreditorNumber(
      remittedDetails?.data,
      remitDetails[0]?.creditorNo,
      false
    );
    const nextCreditorNo = fetchCreditorNumber(
      remittedDetails?.data,
      remitDetails[0]?.creditorNo,
      true
    );

    return { prevCreditorNo, nextCreditorNo };
  }, [remitDetails, remittedDetails]);

  const handleCreditorNavigation = (creditorNum: string) => {
    handleNavigation &&
      handleNavigation(
        getCreditorRemitDetails(remittedDetails?.data, creditorNum)
      );
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{}}
      onSubmit={() => {}}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValidating,
        ...rest
      }: any) => (
        <>
          <div>
            <$Row gutter={8} className="mb-2">
              <$Col span={4}>
                <div className="remt-tile gray-100">
                  <div
                    className="font-weight-bold mb1"
                    data-testid="total-remit-amount"
                  >
                    <a
                      onClick={() =>
                        getRouteUrl.creditor(remitDetails[0]?.creditorNo)
                      }
                      className="font-weight-bold"
                      data-testid="link-creditor"
                    >
                      {creditor}
                    </a>
                  </div>
                  <div className="text-muted">
                    {t("US.COLLECTION.COMMON:COMMON.CREDITOR")}
                  </div>
                </div>
              </$Col>
              <$Col span={3}>
                <div className="remt-tile gray-100">
                  <div
                    className="font-weight-bold mb1"
                    data-testid="total-remit-amount"
                  >
                    <$AmountLabel
                      value={getTotalRemitAmount(selectedRemitRecord)}
                    />
                  </div>
                  <div className="text-muted">
                    {t("US.COLLECTION.ECONOMY:REMIT.TOTAL_AMOUNT")}
                  </div>
                </div>
              </$Col>
              <$Col span={4}>
                <div className="remt-tile gray-100">
                  <div className="font-weight-bold mb1">{journalId}</div>
                  <div className="text-muted">
                    {t("US.COLLECTION.ECONOMY:REMIT.JOURNAL_ID")}
                  </div>
                </div>
              </$Col>
              <$Col span={3}>
                <div className="remt-tile gray-100">
                  <div className="font-weight-bold mb1">{voucherNo}</div>
                  <div className="text-muted">
                    {t("US.COLLECTION.ECONOMY:REMIT.VOUCHER_NO")}
                  </div>
                </div>
              </$Col>
              <$Col span={3}>
                <div className="remt-tile gray-100">
                  <div className="font-weight-bold mb1">
                    <$AccountNoLabel value={accountNo} />
                  </div>
                  <div className="text-muted">
                    {t("US.COLLECTION.ECONOMY:REMITTED_DETAILS.ACCOUNT_NO")}
                  </div>
                </div>
              </$Col>
              <$Col span={3}>
                <div className="remt-tile gray-100">
                  <div
                    className="font-weight-bold mb1"
                    data-testid="vat-status"
                  >
                    {vatStatus}
                  </div>
                  <div className="text-muted">
                    {t("US.COLLECTION.ECONOMY:REMIT.VAT_INFORMATION")}
                  </div>
                </div>
              </$Col>
              <$Col span={4}>
                <div className="remt-tile gray-100">
                  <div
                    className="font-weight-bold mb1"
                    data-testid="remit-account-no"
                  >
                    <$AccountNoLabel value={remitAccountNo} />
                  </div>
                  <div className="text-muted text-nowrap">
                    {t("US.COLLECTION.ECONOMY:REMIT.REMIT_ACCOUNT_NUMBER")}
                  </div>
                </div>
              </$Col>
            </$Row>
          </div>
          <div>
            <$Skeleton
              loading={remitSummaryByCreditor.isLoading}
              active
              paragraph={{ rows: 2 }}
            >
              <RemitDetailsTable />
            </$Skeleton>
          </div>

          <div className="drawer-footer-nav d-flex align-items-center justify-content-center">
            <$Button
              size="small"
              type="dashed"
              disabled={prevCreditorNo == ""}
              onClick={() => handleCreditorNavigation(prevCreditorNo)}
            >
              <LeftOutlined />
              {t("US.COLLECTION.ECONOMY:REMIT.PREV")}
            </$Button>
            {remitDetails[0]?.creditorNo && (
              <div className="drawer-footer-nav-text">
                <small className="d-block bui-label">
                  {t("US.COLLECTION.COMMON:COMMON.CREDITOR")}
                </small>
                <div>
                  <$Select
                    className="drawer-footer-nav-select"
                    style={{ width: 300 }}
                    name="creditor"
                    allOption={false}
                    value={remitDetails[0]?.creditorNo}
                    options={getCreditorCaseList(remittedDetails?.data)}
                    onChange={(value: any) => handleCreditorNavigation(value)}
                  />
                </div>
              </div>
            )}
            <$Button
              size="small"
              type="dashed"
              disabled={nextCreditorNo == ""}
              onClick={() => handleCreditorNavigation(nextCreditorNo)}
            >
              {t("US.COLLECTION.ECONOMY:REMIT.NEXT")}
              <RightOutlined />
            </$Button>
          </div>
        </>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { remit, remitDetails } = state;
  const { remittedDetails } = remitDetails;
  const { remitValidation, remitSummaryByCreditor } = remit;
  return {
    remitValidation,
    remitSummaryByCreditor,
    remittedDetails,
  };
};

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(CreditorRemitDetails);
